import axios from "axios";
import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {add, getUserGroups, getUserTypes} from "./addApi";
import {type ConfigurationError, type ConfigurationResponseDto} from "../types";
import {mapUserGroupsDtoToUserGroups, mapUserTypesDtoToUserTypes} from "../mappers";
import {addFailedText, addFailedUnauthorizedText} from "./constants";
import {clearValidationAction} from "../common/clearValidationAction";
import {type WithState, thunkPrefix} from "../../../store/utils";
import {type AddState} from "./types";
import {type ApiResponse} from "../../../infrastructure/types";
import {Status} from "../../../infrastructure/status";

const initialState: AddState = {
  status: Status.IDLE,
  userGroups: [],
  userGroupsStatus: Status.LOADING,
  userTypes: [],
  userTypesStatus: Status.LOADING,
  validationErrors: {}
};

const reducer = "add";

enum AddSliceThunk {
  Add = "add"
}

export const addAsync = createAsyncThunk<
  ConfigurationResponseDto,
  FormData,
  {rejectValue: ConfigurationError}
>(
  thunkPrefix(reducer, AddSliceThunk.Add),
  async (data: FormData, {fulfillWithValue, rejectWithValue}) => {
    try {
      const response = await add(data);
      const addResult = response?.data?.data;

      return fulfillWithValue(addResult);
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        const response = err?.response?.data as ApiResponse<ConfigurationResponseDto>;
        return rejectWithValue({
            message: response?.messages?.userMessages?.join("\r\n") || err?.response?.status == 401 ? addFailedUnauthorizedText : addFailedText,
          validationErrors: response?.messages?.validationErrors ?? {}
        });
      }
    }
  }
);

export const getUserTypesAsync = createAsyncThunk(
  "add/userTypes",
  async () => (await getUserTypes())?.data?.data
);

export const getUserGroupsAsync = createAsyncThunk(
  "add/userGroups",
  async () => (await getUserGroups())?.data?.data
);

export const addSlice = createSlice({
  name: "add",
  initialState,
  reducers: {
    clearValidation: clearValidationAction
  },
  extraReducers: builder => {
    builder
      .addCase(addAsync.pending, state => {
        state.status = Status.LOADING;
        state.validationErrors = {};
      })
      .addCase(addAsync.fulfilled, state => {
        state.status = Status.IDLE;
      })
      .addCase(addAsync.rejected, (state, action) => {
        state.status = Status.FAILED;
        state.validationErrors = action.payload.validationErrors;
      });

    builder
      .addCase(getUserGroupsAsync.pending, state => {
        state.userGroupsStatus = Status.LOADING;
      })
      .addCase(getUserGroupsAsync.fulfilled, (state, action) => {
        state.userGroups = mapUserGroupsDtoToUserGroups(action.payload);
        state.userGroupsStatus = Status.IDLE;
      })
      .addCase(getUserGroupsAsync.rejected, state => {
        state.userGroupsStatus = Status.FAILED;
      });

    builder
      .addCase(getUserTypesAsync.pending, state => {
        state.userTypesStatus = Status.LOADING;
      })
      .addCase(getUserTypesAsync.fulfilled, (state, action) => {
        state.userTypes = mapUserTypesDtoToUserTypes(action.payload);
        state.userTypesStatus = Status.IDLE;
      })
      .addCase(getUserTypesAsync.rejected, state => {
        state.userTypesStatus = Status.FAILED;
      });
  }
});

export const selectStatus = (state: WithState<AddState, typeof reducer>) => state.add.status;
export const selectUserGroups = (state: WithState<AddState, typeof reducer>) =>
  state.add.userGroups;
export const selectUserGroupsStatus = (state: WithState<AddState, typeof reducer>) =>
  state.add.userGroupsStatus;
export const selectUserTypes = (state: WithState<AddState, typeof reducer>) => state.add.userTypes;
export const selectUserTypesStatus = (state: WithState<AddState, typeof reducer>) =>
  state.add.userTypesStatus;
export const selectAddValidationErrors = (state: WithState<AddState, typeof reducer>) =>
  state.add.validationErrors;

export const {clearValidation} = addSlice.actions;

export const addReducer = addSlice.reducer;
